/**
 * ComboChart — dual-axis combo: volume BARS on the left axis + a rate LINE on
 * the right axis. The classic "is the engine efficient AND growing?" view:
 * revenue bars + MER× line, or spend bars + ROAS line. The two series live on
 * independent scales so a small ratio (e.g. 3.2×) and a large dollar figure
 * (e.g. $48k) both stay readable instead of one flattening the other.
 *
 * Bars use var(--accent); the rate line uses var(--c2) with small dots. The
 * left axis formats with moneyFmt (or a plain count); the right axis appends
 * lineSuffix (e.g. "×" or "%"). Matches TrendChart's restraint — light
 * gridlines, ~12 autoskip x labels, no chart junk.
 */

import { useMemo, useRef, useState } from "react";
import { moneyFmt } from "./cards.js";

export interface ComboPoint {
  label: string;
  /** Left-axis volume value (drawn as a bar). */
  bar: number;
  /** Right-axis rate value (drawn as the line). */
  line: number;
}

const W = 620;
const H = 240;
const PAD = { l: 50, r: 46, t: 12, b: 26 };
const IW = W - PAD.l - PAD.r;
const IH = H - PAD.t - PAD.b;

export function ComboChart({
  points,
  barLabel,
  lineLabel,
  barMoney = true,
  lineSuffix = "",
}: {
  points: ComboPoint[];
  /** Legend/tooltip name for the bar (left-axis) series. */
  barLabel: string;
  /** Legend/tooltip name for the line (right-axis) series. */
  lineLabel: string;
  /** Format the left axis + bar values as money (default) vs a plain count. */
  barMoney?: boolean;
  /** Appended to right-axis + line values, e.g. "×" or "%" (default none). */
  lineSuffix?: string;
}): React.ReactElement {
  const [hover, setHover] = useState<number | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  // Bars start at zero; the rate line gets a small headroom factor so its peak
  // doesn't kiss the top edge. Both guard against an all-zero series.
  const barMax = useMemo(() => Math.max(1, ...points.map((p) => p.bar)) * 1.08, [points]);
  const lineMax = useMemo(() => Math.max(0.0001, ...points.map((p) => p.line)) * 1.12, [points]);

  if (points.length === 0) {
    return <div style={{ color: "var(--faint)", padding: "18px 0" }}>No data yet.</div>;
  }

  const barFmt = (v: number): string => (barMoney ? moneyFmt(v) : Math.round(v).toLocaleString());
  // Rate values: 1 decimal unless they're large enough to round cleanly.
  const lineFmt = (v: number): string => `${Math.abs(v) >= 100 ? Math.round(v).toLocaleString() : v.toFixed(1)}${lineSuffix}`;

  const xBar = (i: number): number => PAD.l + (i + 0.5) * (IW / points.length);
  const yBar = (v: number): number => PAD.t + IH - (v / barMax) * IH;
  const yLine = (v: number): number => PAD.t + IH - (v / lineMax) * IH;

  const barW = (IW / points.length) * 0.6;
  const gridVals = [0, 0.5, 1]; // fractions of each axis — shared gridline rows

  // Rate line path + dots (skips non-finite values defensively).
  const linePts = points
    .map((p, i): [number, number] | null => (Number.isFinite(p.line) ? [xBar(i), yLine(p.line)] : null))
    .filter((q): q is [number, number] => q !== null);
  const linePath = linePts.map((q, i) => `${i ? "L" : "M"}${q[0].toFixed(1)},${q[1].toFixed(1)}`).join(" ");

  const onMove = (e: React.MouseEvent<SVGSVGElement>): void => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return;
    const px = ((e.clientX - rect.left) / rect.width) * W;
    const idx = Math.floor((px - PAD.l) / (IW / points.length));
    setHover(Math.max(0, Math.min(points.length - 1, idx)));
  };

  const hovered = hover === null ? null : points[hover] ?? null;
  // X labels: show every point up to 7, then thin to ~12 evenly spaced.
  const step = points.length <= 7 ? 1 : Math.max(1, Math.ceil(points.length / 12));

  return (
    <div style={{ position: "relative" }}>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${W} ${H}`}
        width="100%"
        onMouseMove={onMove}
        onMouseLeave={() => setHover(null)}
        style={{ display: "block", cursor: "crosshair" }}
        role="img"
        aria-label={`${barLabel} bars with ${lineLabel} line`}
      >
        {/* Gridlines + dual axis labels (left = volume, right = rate) */}
        {gridVals.map((f, i) => {
          const yy = PAD.t + IH - f * IH;
          return (
            <g key={i}>
              <line x1={PAD.l} y1={yy} x2={W - PAD.r} y2={yy} stroke="var(--grid)" />
              <text x={PAD.l - 4} y={yy + 3} textAnchor="end" fill="var(--body-faint)" fontSize={10} className="dd-num">
                {barFmt(barMax * f)}
              </text>
              <text x={W - PAD.r + 4} y={yy + 3} textAnchor="start" fill="var(--c2)" fontSize={10} className="dd-num">
                {lineFmt(lineMax * f)}
              </text>
            </g>
          );
        })}

        {/* Volume bars (left axis) */}
        {points.map((p, i) => {
          const yy = yBar(p.bar);
          return (
            <rect
              key={p.label}
              x={xBar(i) - barW / 2}
              y={yy}
              width={barW}
              height={Math.max(0, PAD.t + IH - yy)}
              rx={2}
              fill="var(--accent)"
              opacity={hover === null || hover === i ? 1 : 0.55}
            />
          );
        })}

        {/* Rate line (right axis) + dots */}
        {linePts.length >= 2 && (
          <path d={linePath} fill="none" stroke="var(--c2)" strokeWidth={2.2} strokeLinejoin="round" strokeLinecap="round" />
        )}
        {points.map((p, i) =>
          Number.isFinite(p.line) ? (
            <circle key={p.label} cx={xBar(i)} cy={yLine(p.line)} r={hover === i ? 3.4 : 2.4} fill="var(--c2)" />
          ) : null,
        )}

        {/* X labels — capped ~12 with autoskip */}
        {points.map((p, i) =>
          i % step === 0 ? (
            <text key={p.label} x={xBar(i)} y={H - 9} textAnchor="middle" fill="var(--body-faint)" fontSize={9}>
              {p.label}
            </text>
          ) : null,
        )}

        {/* Hover crosshair */}
        {hover !== null && (
          <line x1={xBar(hover)} y1={PAD.t} x2={xBar(hover)} y2={PAD.t + IH} stroke="var(--body-faint)" strokeDasharray="3 3" />
        )}
      </svg>

      {hovered && hover !== null && (
        <div
          style={{
            position: "absolute",
            left: `${((xBar(hover) / W) * 100).toFixed(1)}%`,
            top: 0,
            transform: `translateX(${hover > points.length / 2 ? "-104%" : "6px"})`,
            background: "var(--surface)",
            border: "1px solid var(--border)",
            borderRadius: 8,
            boxShadow: "var(--shadow-pop)",
            padding: "8px 11px",
            fontSize: 12,
            pointerEvents: "none",
            whiteSpace: "nowrap",
            zIndex: 5,
          }}
        >
          <div style={{ color: "var(--text)", fontWeight: 600, marginBottom: 3 }}>{hovered.label}</div>
          <div style={{ color: "var(--body)" }}>
            <i style={dot("var(--accent)")} />
            {barLabel}: <b className="dd-num" style={{ color: "var(--num)" }}>{barFmt(hovered.bar)}</b>
          </div>
          <div style={{ color: "var(--body)" }}>
            <i style={dot("var(--c2)")} />
            {lineLabel}: <b className="dd-num" style={{ color: "var(--num)" }}>{lineFmt(hovered.line)}</b>
          </div>
        </div>
      )}
    </div>
  );
}

function dot(bg: string): React.CSSProperties {
  return { display: "inline-block", width: 9, height: 9, borderRadius: 3, marginRight: 5, background: bg };
}
